import mixpanel from 'mixpanel-browser';
import { getLocalData } from 'utils/localDataStore';
import _has from 'lodash/has';
import _get from 'lodash/get';
import _isUndefined from 'lodash/isUndefined';
import _isEmpty from 'lodash/isEmpty';
import { getFirebase } from 'react-redux-firebase';
import qs from 'qs';
import { emitter } from '@marvelapp/react-ab-test';
import GA4 from 'utils/ga4Service';
import Config from 'utils/getEnvConfig';
import {
  isBot,
  isLocalStorageAllowed,
  getParsedUserAgeInfo,
  getParentShareUrl,
  getSubdomain,
  emailRegex,
} from 'utils/stringUtils';
import { isLocalhost, parseCloudflareIP } from 'utils/networkUtils';
import { getStore } from 'configureStore';
import { getEmbeddedSubBrand, isWebView } from 'utils/embedded';
import { convertToPattern } from 'containers/Assessment/utils';
import { getIsClientAdmin } from 'containers/Auth/utils';
import { trackCatch } from 'utils/sentryApiCatch';
import FirebaseAnalyticsService from './firebaseAnalyticsService';
import { getBasename, removeBasename } from './history';
import { getCookie } from './cookieStore';
import { getHistoricalSourceData as getHistorical } from './sourceTrackingService';

const DEBUG = false;

// enable only in production mode
const activeMixpanel = !isLocalhost && !isBot();

if (activeMixpanel) {
  mixpanel.init(Config.MIXPANEL.TOKEN, {
    api_host: 'https://userdata.crediblemind.com',
    ignore_dnt: true,
    record_sessions_percent: 10,
    record_min_ms: 8000,
    record_block_selector: '',
    record_mask_text_selector: '',
  });
  registerUserAgentWithBotIgnore();
  registerTouch();
  getReferrerGroup();
  getUtmTags();
}

export const injectAssessmentResultsABData = (
  props,
  shouldABTest,
  isVariantB,
) => {
  const eventProps = props;
  if (shouldABTest) {
    eventProps.assessmentResultsDesignVariant = isVariantB
      ? 'variantB'
      : 'variantA';
  }
  return eventProps;
};

function registerTouch() {
  const touchURL = document.URL;

  mixpanel.people.set_once('First Touch URL', touchURL);
  mixpanel.people.set('Last Touch URL', touchURL);
}

function getReferrerGroup() {
  let referrerGroup = 'Other';

  if (/((http(s)?)?:\/\/)?google.([^/?]*)/.test(document.referrer)) {
    const params = qs.parse(document.URL.split('?')[1]);
    if (params.utm_source === 'adwords') referrerGroup = 'Google Paid';
    else referrerGroup = 'Google Search';
  } else if (/((http(s)?)?:\/\/)?bing.([^/?]*)/.test(document.referrer)) {
    referrerGroup = 'Bing';
  } else if (/((http(s)?)?:\/\/)?yahoo.([^/?]*)/.test(document.referrer)) {
    referrerGroup = 'Yahoo';
  } else if (
    /((http(s)?)?:\/\/)?facebook.([^/?]*)/.test(document.referrer) ||
    /((http(s)?)?:\/\/)?fb.([^/?]*)/.test(document.referrer)
  ) {
    referrerGroup = 'Facebook';
  } else if (
    /((http(s)?)?:\/\/)?twitter.([^/?]*)/.test(document.referrer) ||
    /((http(s)?)?:\/\/)?t.co/.test(document.referrer)
  ) {
    referrerGroup = 'Twitter';
  } else if (/((http(s)?)?:\/\/)?linkedin.([^/?]*)/.test(document.referrer)) {
    referrerGroup = 'LinkedIn';
  } else if (/((http(s)?)?:\/\/)?quora.([^/?]*)/.test(document.referrer)) {
    referrerGroup = 'Quora';
  } else if (/((http(s)?)?:\/\/)?duckduckgo.([^/?]*)/.test(document.referrer)) {
    referrerGroup = 'DuckDuckGo';
  }

  mixpanel.people.set_once('Initial Referrer Domain Group', referrerGroup);
}

function getUtmTags() {
  const params = qs.parse(document.URL.split('?')[1]);

  const firstTouch = {};
  const lastTouch = {
    'utm_source [last touch]': '$direct',
    'utm_campaign [last touch]': undefined,
  };

  if (params.utm_source) {
    firstTouch['utm_source [first touch]'] = params.utm_source;
    lastTouch['utm_source [last touch]'] = params.utm_source;
  }
  if (params.utm_campaign) {
    firstTouch['utm_campaign [first touch]'] = params.utm_campaign;
    lastTouch['utm_campaign [last touch]'] = params.utm_campaign;
  }

  if (params.utm_source === 'email') {
    lastTouch.from_email = true;
    if (params.utm_campaign) {
      lastTouch.email_campaign = params.utm_campaign;
    }
  }

  mixpanel.people.set_once(firstTouch);
  mixpanel.people.set(lastTouch);
  mixpanel.register(lastTouch);
}

function registerUserAgentWithBotIgnore() {
  const { userAgent } = navigator;
  mixpanel.register({ 'User Agent': userAgent });

  if (isBot()) {
    mixpanel.register({ $ignore: true });
  }
}

let ipAddress;

const getIpAddress = async () => {
  if (!ipAddress) {
    ipAddress = await parseCloudflareIP();
  }
  return ipAddress;
};

// Initial IP fetch
getIpAddress();

// Refresh IP periodically
setInterval(getIpAddress, 60000);

let identified = false;

const identify = async () => {
  const { isAICC } = await getClientRelatedData();
  if (isAICC) {
    await waitForAICCData();
  }

  const firebase = await getFirebase();
  const store = getStore();
  const state = store.getState();
  const auth = _get(state, 'firebase.auth');
  const aiccUser = _get(state, 'main.aiccUser', {});
  const sessionAnonymousId = _get(state, 'auth.anonymousId');
  const id =
    _get(aiccUser, 'user_id') ||
    _get(auth, 'uid') ||
    getLocalData('userIdTemp') ||
    sessionAnonymousId;
  if (DEBUG || activeMixpanel) {
    identified = true;
    if (DEBUG) {
      console.log('mixpanel.identify', { id, auth, aiccUser });
    }
    if (activeMixpanel) {
      mixpanel.identify(id);
      firebase.analytics().setUserId(id);
    }
  }
};

const getAuth = store => {
  const state = store.getState();
  if (!_get(state, 'firebase.auth.isLoaded')) return undefined;
  return _get(state, 'firebase.auth', {});
};

const getProfile = store => {
  const state = store.getState();
  if (!_get(state, 'firebase.profile.isLoaded')) return undefined;
  return _get(state, 'firebase.profile', {});
};

const getAgeGenderMappingSiteConfig = store => {
  const state = store.getState();
  return _get(state, 'main.siteConfig', []).find(
    item => item.title === 'Age and Gender Mappings',
  );
};

const getClinicMode = store => {
  const state = store.getState();
  return _get(state, 'main.clinicMode', false);
};

const getClientDetails = store => {
  const state = store.getState();
  return _get(state, 'main.clientDetails', {});
};

const getAICCUser = store => {
  const state = store.getState();
  return _get(state, 'main.aiccUser', {});
};

const getBirthdayFromAssessmentAnswers = async () => {
  const store = getStore();
  const state = store.getState();
  if (_get(state, 'assessment.answers.birthdayMonthYear')) {
    const userAgeInfo = getParsedUserAgeInfo(
      _get(state, 'assessment.answers.birthdayMonthYear'),
    );
    return userAgeInfo;
  }
  return {};
};

const getFirebaseId = async () =>
  new Promise(resolve => {
    const store = getStore();
    let auth = getAuth(store);
    if (auth !== undefined) {
      resolve(auth.uid);
    } else {
      const unsubscribe = store.subscribe(() => {
        auth = getAuth(store);
        if (auth !== undefined) {
          unsubscribe();
          resolve(auth.uid);
        }
      });
    }
  });

const getAnonymousUserId = async () =>
  new Promise(resolve => {
    const store = getStore();
    let clinicMode = getClinicMode(store);
    if (clinicMode !== undefined) {
      const storeState = store.getState();
      const sessionAnonymousId = _get(storeState, 'auth.anonymousId');
      const userIdTemp = getLocalData('userIdTemp');
      // console.log({ sessionAnonymousId, userIdTemp, clinicMode });
      if (clinicMode) {
        resolve(sessionAnonymousId || userIdTemp);
      } else {
        resolve(userIdTemp || sessionAnonymousId);
      }
    } else {
      const unsubscribe = store.subscribe(() => {
        clinicMode = getAuth(store);
        if (clinicMode !== undefined) {
          unsubscribe();
          const storeState = store.getState();
          const sessionAnonymousId = _get(storeState, 'auth.anonymousId');
          const userIdTemp = getLocalData('userIdTemp');
          if (clinicMode) {
            resolve(sessionAnonymousId || userIdTemp);
          } else {
            resolve(userIdTemp || sessionAnonymousId);
          }
        }
      });
    }
  });

const parseIsInternalData = ({ auth, clientDetails, profile }) =>
  profile.role === 'admin' ||
  profile.role === 'contentAdmin' ||
  getIsClientAdmin(profile, clientDetails) ||
  (profile.email || '').includes('@crediblemind.co') ||
  (!!getCookie('backdoorUser') &&
    !!auth &&
    !auth.isEmpty &&
    auth.uid &&
    getCookie('backdoorUser') === auth.uid);

export const parseClientSubBrand = ({ clientDetails, profile }) => {
  const subBrandConfig = _get(clientDetails, 'metadata.subBrandConfig', {});

  if (!_isEmpty(subBrandConfig)) {
    const subBrand = _get(profile, subBrandConfig.path);
    if (_get(subBrandConfig, 'expectedValues', []).includes(subBrand))
      return subBrand;
  }

  return undefined;
};

const parseDemographicsData = profile => {
  const data = {};
  if (profile.gender) {
    data.userGender = profile.gender;
  }
  if (profile.genderLGBTQp) {
    data.userGenderLGBTQp = profile.genderLGBTQp;
  }
  if (profile.dob) {
    data.userAge = profile.dob;
  }
  if (profile.birthdayMonthYear) {
    const userAgeInfo = getParsedUserAgeInfo(profile.birthdayMonthYear);
    data.userBirthdayMonth = userAgeInfo.userBirthdayMonth;
    data.userParsedAge = userAgeInfo.userParsedAge;
  }
  if (profile.industry) {
    data.userIndustry = profile.industry;
  }
  if (profile.isEmpty) {
    data.userInsurance = getLocalData('insurance');
  } else if (Array.isArray(profile.insurance))
    data.userInsurance = profile.insurance.join(',');
  else data.userInsurance = profile.insurance;

  return data;
};

const parseAssessmentDemographicsData = (
  userId,
  ageGenderMappingSiteConfig,
) => {
  const data = {};

  const stringified = getLocalData('assessmentDemographics');
  // Check if stringified is a valid JSON string
  try {
    const assessmentDemographics = stringified ? JSON.parse(stringified) : {};
    if (assessmentDemographics.id === userId) {
      if (assessmentDemographics.gender) {
        data.userGender = convertToPattern(
          'gender',
          assessmentDemographics.gender,
          ageGenderMappingSiteConfig,
        );
      }
      if (assessmentDemographics.genderOther) {
        data.userGenderOther = assessmentDemographics.genderOther;
      }
      if (assessmentDemographics.genderLGBTQp) {
        data.userGenderLGBTQp = convertToPattern(
          'genderLGBTQp',
          assessmentDemographics.genderLGBTQp,
          ageGenderMappingSiteConfig,
        );
      }
      if (assessmentDemographics.dob) {
        data.userAge = assessmentDemographics.dob;
      }
      if (assessmentDemographics.birthdayMonthYear) {
        const userAgeInfo = getParsedUserAgeInfo(
          assessmentDemographics.birthdayMonthYear,
        );
        data.userBirthdayMonth = userAgeInfo.userBirthdayMonth;
        data.userParsedAge = userAgeInfo.userParsedAge;
      }
      if (assessmentDemographics.industry) {
        data.userIndustry = convertToPattern(
          'industry',
          assessmentDemographics.industry,
          ageGenderMappingSiteConfig,
        );
      }
      if (assessmentDemographics.insurance) {
        data.userInsurance = assessmentDemographics.insurance;
      }
    }
  } catch (e) {
    console.error('Failed to parse assessmentDemographics data:', e);
  }

  return data;
};

const parseCustomData = profile => {
  const data = {};
  if (_has(profile, 'custom.customConsent')) {
    data.customConsent = profile.custom.customConsent;
  }
  if (_has(profile, 'custom.mid')) {
    if (Array.isArray(profile.custom.mid)) {
      data.MID = profile.custom.mid.join(',');
    } else if (typeof profile.custom.mid === 'string') {
      data.MID = profile.custom.mid;
    }
  }

  return data;
};

const parseSAMLData = profile => {
  const samlData = _get(profile, 'samlData', {});
  Object.keys(samlData).forEach(key => {
    const value = samlData[key];

    if (emailRegex.test(value)) {
      delete samlData[key];
    }
  });

  return samlData;
};

const getProfileRelatedData = async () =>
  new Promise(resolve => {
    const store = getStore();
    let auth = getAuth(store);
    let clientDetails = getClientDetails(store);
    let profile = getProfile(store);

    const parseProfileRelatedData = (authData, profileData, clientData) => ({
      demographicsData: parseDemographicsData(profileData),
      samlData: parseSAMLData(profileData),
      customData: parseCustomData(profileData),
      isSignedIn: !profileData.isEmpty,
      isInternal: parseIsInternalData({
        auth: authData,
        clientDetails: clientData,
        profile: profileData,
      }),
      clientSubBrand: parseClientSubBrand({
        clientDetails: clientData,
        profile: profileData,
      }),
    });

    if (
      auth !== undefined &&
      profile !== undefined &&
      clientDetails !== undefined
    ) {
      resolve(parseProfileRelatedData(auth, profile, clientDetails));
    } else {
      const unsubscribe = store.subscribe(() => {
        auth = getAuth(store);
        clientDetails = getClientDetails(store);
        profile = getProfile(store);

        if (
          auth !== undefined &&
          profile !== undefined &&
          clientDetails !== undefined
        ) {
          unsubscribe();
          resolve(parseProfileRelatedData(auth, profile, clientDetails));
        }
      });
    }
  });

const getClientRelatedData = async () =>
  new Promise(resolve => {
    const store = getStore();
    let clientDetails = getClientDetails(store);

    const parseClientRelatedData = clientData => ({
      isAICC: _get(clientData, 'aicc', false),
      brandSignin: _get(clientDetails, 'signIn'),
      brandSector: _get(clientDetails, 'sector'),
      clientGroup: _get(clientDetails, 'clientGroup.formalShortName'),
      brandExcludedResourceTypes: _get(clientDetails, 'excludeResourceTypes'),
    });

    if (clientDetails !== undefined) {
      resolve(parseClientRelatedData(clientDetails));
    } else {
      const unsubscribe = store.subscribe(() => {
        clientDetails = getClientDetails(store);

        if (clientDetails !== undefined) {
          unsubscribe();
          resolve(parseClientRelatedData(clientDetails));
        }
      });
    }
  });

const getUserRole = (store, currentUrl) => {
  // Check if the current URL corresponds to the Admin Portal with client-admin
  if (currentUrl?.includes('client-admin')) {
    const profile = getProfile(store);
    const clientDetails = getClientDetails(store);

    if (profile.role === 'admin') return 'admin';
    if (getIsClientAdmin(profile, clientDetails)) return 'clientAdmin';
  }
  return null;
};

const getAICCData = async () =>
  new Promise(resolve => {
    const store = getStore();
    let aiccUser = getAICCUser(store);
    if (aiccUser !== undefined) {
      const data = {
        organizationName: _get(aiccUser, 'organization_name'),
        institutionName: _get(aiccUser, 'institution_name'),
        studentId: _get(aiccUser, 'student_id'),
      };
      resolve(data);
    } else {
      const unsubscribe = store.subscribe(() => {
        aiccUser = getAICCUser(store);
        if (aiccUser !== undefined) {
          unsubscribe();
          const data = {
            organizationName: _get(aiccUser, 'organization_name'),
            institutionName: _get(aiccUser, 'institution_name'),
            studentId: _get(aiccUser, 'student_id'),
          };
          resolve(data);
        }
      });
    }
  });

const waitForAICCData = async () =>
  new Promise(resolve => {
    const store = getStore();
    let user = getAICCUser(store);
    if (!_isEmpty(user)) {
      resolve(true);
    } else {
      const unsubscribe = store.subscribe(() => {
        user = getAICCUser(store);
        if (!_isEmpty(user)) {
          unsubscribe();
          resolve(true);
        }
      });
    }
  });

const applyHistoricalLoginSource = sourceData => {
  if (!activeMixpanel || !sourceData || isBot()) {
    return;
  }

  const historicalTouch = {
    attribution_source: 'historical',
  };

  if (sourceData.utm_source) {
    historicalTouch['utm_source [returning user]'] = sourceData.utm_source;
    historicalTouch.returning_utm_source = sourceData.utm_source;
  }

  if (sourceData.utm_campaign) {
    historicalTouch['utm_campaign [returning user]'] = sourceData.utm_campaign;
    historicalTouch.returning_utm_campaign = sourceData.utm_campaign;
  }

  // Special handling for email sources
  if (sourceData.utm_source === 'email') {
    historicalTouch.returning_from_email = true;
    if (sourceData.utm_campaign) {
      historicalTouch['email_campaign [returning]'] = sourceData.utm_campaign;
      historicalTouch.returning_email_campaign = sourceData.utm_campaign;
    }
  }

  // Register as super properties (for all future events)
  mixpanel.register(historicalTouch);

  // Set as user profile properties
  mixpanel.people.set(historicalTouch);
};

const MixpanelActions = {
  // init: token => {
  //   if (activeMixpanel) {
  //     mixpanel.init(token);
  //   }
  // },
  applyHistoricalLoginSource,
  identify: async () => {
    // Runtime bot check
    if (isBot()) return;
    await identify();
  },
  alias: firebaseId => {
    // Runtime bot check
    if (isBot()) return;

    const id = getLocalData('userIdTemp');
    if (DEBUG) console.log('mixpanel.alias', firebaseId, id);
    if (activeMixpanel) mixpanel.alias(firebaseId, id);
  },
  track: async (name, props) => {
    // Runtime bot check - skip tracking for bots
    if (isBot()) {
      if (DEBUG) console.log('mixpanel.track skipped - bot detected', name);
      return;
    }

    if (!identified) {
      await identify();
    }

    // Apply historical login source for returning users
    // This is called here to ensure user is identified first
    if (name === 'Login - Successful' && !props.hasUtmParameters) {
      const historicalData = getHistorical();
      if (historicalData) {
        applyHistoricalLoginSource(historicalData);
      }
    }

    const currentUrl = window.location.href;
    const basename = getBasename();

    const { referrer } = document;
    const store = getStore();
    const heroVariant = emitter.getActiveVariant('hero-variant');
    const assessmentResultsVariant = emitter.getActiveVariant(
      'assessment-results-lists',
    );
    const firstTimeUser = getLocalData('firstTimeUser');
    let firstTimeUserFinal = 'Returning';
    if (_isUndefined(firstTimeUser)) {
      firstTimeUserFinal = 'Undefined';
    } else if (firstTimeUser === 'true') {
      firstTimeUserFinal = 'New';
    }

    const userRole = getUserRole(store, currentUrl);

    let language = 'Undefined';
    if (isLocalStorageAllowed()) {
      language = localStorage.getItem('language');
      if (language === null) language = 'english';
    }

    const referrerData = {};
    if (isInternal) {
      referrerData.referrer = null;
    }

    const embeddedContainerUrl = getParentShareUrl();
    const embeddedSubBrand = getEmbeddedSubBrand();
    const isWebViewMode = isWebView();

    const brand = getSubdomain() || getLocalData('brand');
    const clinicMode = getClinicMode(store);
    const ageGenderMappingSiteConfig = getAgeGenderMappingSiteConfig(store);

    let demographicsData = {};
    let samlData = {};
    let customData = {};
    let isSignedIn = false;
    let isInternal = false;
    let firebaseId;
    let anonymousId;
    let isAICC = false;
    let brandSignin;
    let brandExcludedResourceTypes;
    let brandSector;
    let clientGroup;
    let assessmentDemographicsData = {};
    let birthdayFromAssessmentAnswers = {};
    let aiccData = {};
    let clientSubBrand;
    try {
      [
        {
          demographicsData,
          samlData,
          customData,
          isSignedIn,
          isInternal,
          clientSubBrand,
        },
        firebaseId,
        anonymousId,
        {
          isAICC,
          brandSignin,
          brandSector,
          clientGroup,
          brandExcludedResourceTypes,
        },
      ] = await Promise.all([
        getProfileRelatedData(),
        getFirebaseId(),
        getAnonymousUserId(),
        getClientRelatedData(),
      ]);
      assessmentDemographicsData = !isSignedIn
        ? parseAssessmentDemographicsData(
            anonymousId,
            ageGenderMappingSiteConfig,
          )
        : {};
      birthdayFromAssessmentAnswers = !isSignedIn
        ? await getBirthdayFromAssessmentAnswers()
        : {};
      aiccData = isAICC ? await getAICCData() : {};
    } catch (error) {
      trackCatch(error);
    }

    const customOnboardingReport = JSON.parse(
      getLocalData(`${brand}_customOnboardingReport`) || '{}',
    );
    const onboardingData = {};
    Object.keys(customOnboardingReport).forEach(key => {
      if (_get(customOnboardingReport, [key, 'value'])) {
        onboardingData[key] = _get(customOnboardingReport, [key, 'value']);
      }
    });

    let finalProps = {
      ...props,
      brand: brand || 'none',
      firstTimeUser: firstTimeUserFinal,
      homeVariant: heroVariant || 'none',
      assessmentResultsVariant: assessmentResultsVariant || 'none',
      initial_id: getLocalData('userIdTemp'),
      publicMode: clinicMode,
      firebase_id: firebaseId,
      user_ip: ipAddress,
      language,
      ...(embeddedContainerUrl ? { containerUrl: embeddedContainerUrl } : {}),
      ...(embeddedSubBrand || clientSubBrand
        ? { subBrand: embeddedSubBrand || clientSubBrand }
        : {}),
      $current_url: currentUrl,
      $referrer: referrer,
      ...referrerData,
      ...(isWebViewMode ? { isWebView: isWebViewMode } : {}),
      // Client related data
      isAICC,
      brandSignin,
      ...(_isEmpty(brandExcludedResourceTypes)
        ? {}
        : { brandExcludedResourceTypes }),
      brandSector,
      clientGroup,
      // User related data
      isInternal,
      isSignedIn,
      ...(userRole ? { userRole } : {}),
      ...aiccData,
      ...demographicsData,
      ...samlData,
      ...customData,
      ...birthdayFromAssessmentAnswers,
      ...assessmentDemographicsData,
      ...onboardingData,
    };

    if (basename) {
      finalProps = Object.entries(finalProps).reduce((acc, [key, value]) => {
        acc[key] = typeof value === 'string' ? removeBasename(value) : value;
        return acc;
      }, {});
    }

    if (!finalProps.name) finalProps.name = name;

    if (DEBUG) console.log('mixpanel.track', name, finalProps);
    if (activeMixpanel) {
      mixpanel.track(name, finalProps);
      GA4.track(name, finalProps);
      FirebaseAnalyticsService.addEvent(name, finalProps);
    }
  },
  // session_end: userId => {
  //   if (activeMixpanel) {
  //     const token = Config.MIXPANEL.TOKEN;
  //     const startTime =
  //       // eslint-disable-next-line dot-notation
  //       mixpanel['persistence']['props']['__timers']['Session ended'];
  //     let duration = new Date().getTime() - startTime;
  //     duration = parseFloat((duration / 1000).toFixed(3));
  //     const data = {
  //       event: 'Session ended',
  //       properties: {
  //         ...mixpanel._.info.properties(),
  //         ...mixpanel.persistence.properties(),
  //         token,
  //         distinct_id: userId,
  //         $duration: duration,
  //         name: 'Session ended',
  //       },
  //     };
  //     const encodedData = btoa(JSON.stringify(data));

  //     const req = new XMLHttpRequest();
  //     req.open(
  //       'GET',
  //       `https://api.mixpanel.com/track/?data=${encodedData}&ip=1&_=${Date.now()}`,
  //       false,
  //     );
  //     const headers = mixpanel.get_config('xhr_headers');
  //     _forEach(headers, (headerValue, headerName) => {
  //       req.setRequestHeader(headerName, headerValue);
  //     });
  //     req.withCredentials = true;
  //     req.send(null);
  //   }
  // },
  // time_event: name => {
  //   if (activeMixpanel) mixpanel.time_event(name);
  // },
  people: {
    set: props => {
      // Runtime bot check
      if (isBot()) return;

      const brand = getLocalData('brand');
      if (DEBUG) console.log('mixpanel.people.set', props);
      if (activeMixpanel) {
        mixpanel.people.set(props);
        FirebaseAnalyticsService.setUserProfile({ ...props, brand });
      }
    },
  },
  reset: () => {
    if (DEBUG || activeMixpanel) {
      identified = false;
      if (activeMixpanel) mixpanel.reset();
    }
  },
  forceIdentify: () => {
    if (DEBUG || activeMixpanel) {
      identified = false;
    }
  },
};

export default MixpanelActions;
